import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  PieChart, BarChart as BarChartIcon, Calendar, AlertTriangle, Download,
  DollarSign, TrendingUp, Percent
} from 'lucide-react';
import { toast } from 'react-toastify';
import { 
  PieChart as RechartsePieChart, 
  Pie, 
  BarChart, 
  CartesianGrid, 
  XAxis, 
  YAxis, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  Cell
} from 'recharts';

const AdminAccountingPanel = () => {
  const orders = useSelector((state) => state.orders?.allOrders || []);
  
  // Estados locales
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [viewMode, setViewMode] = useState('table'); // 'table', 'pie', 'BarChartIcon'
  
  // Categorías de gastos esperadas
  const expenseCategories = ['mercaderia', 'aduana', 'envio', 'extras'];
  
  // Estados a excluir
  const excludedStatuses = ['Pago pendiente', 'Pedido cancelado'];
  
  // Filtrar órdenes válidas para contabilidad
  const validOrders = orders.filter(order => 
    !excludedStatuses.includes(order.status)
  );
  
  // Aplicar filtro de fechas
  const filteredOrders = validOrders.filter(order => {
    const orderDate = new Date(order.createdAt || order.created_at);
    const orderDateString = orderDate.toISOString().split('T')[0];
    
    const matchesDateRange = (
      !dateRange.start || orderDateString >= dateRange.start
    ) && (
      !dateRange.end || orderDateString <= dateRange.end
    );

    return matchesDateRange;
  });

  // Función para determinar gastos faltantes
  const getMissingExpenses = (expenses, order) => {
    // Definimos las categorías a revisar (eliminamos "extras" ya que es situacional)
    let categoriesToCheck = expenseCategories.filter(cat => cat !== "extras");
    
    // Si es pickup, no requerimos "envio"
    if (order?.shipping_address?.type === "pickup") {
      categoriesToCheck = categoriesToCheck.filter(cat => cat !== "envio");
    }
    
    // Si no hay gastos, devolvemos todas las categorías a revisar
    if (!expenses) return categoriesToCheck;
    
    // Devolvemos solo las categorías que no tienen valor
    return categoriesToCheck.filter(category => 
      expenses[category] === undefined || expenses[category] === null
    );
  };

  // Calcular totales
  const calculateTotals = () => {
    let totalRevenue = 0;
    let totalExpenses = {
      mercaderia: 0,
      aduana: 0,
      envio: 0,
      extras: 0
    };
    let totalProfit = 0;

    filteredOrders.forEach(order => {
      const orderTotal = parseFloat(order.total) || 0;
      totalRevenue += orderTotal;
      
      // Sumar gastos
      if (order.gastos) {
        Object.entries(order.gastos).forEach(([category, amount]) => {
          if (totalExpenses[category] !== undefined) {
            totalExpenses[category] += parseFloat(amount) || 0;
          }
        });
      }
    });

    // Calcular gasto total
    const totalExpensesSum = Object.values(totalExpenses).reduce((sum, value) => sum + value, 0);
    
    // Calcular ganancia
    totalProfit = totalRevenue - totalExpensesSum;
    
    // Calcular porcentaje de rentabilidad
    const profitPercentage = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    return {
      revenue: totalRevenue,
      expenses: totalExpenses,
      expensesSum: totalExpensesSum,
      profit: totalProfit,
      profitPercentage
    };
  };

  const totals = calculateTotals();

  // Preparar datos para gráficos
  const prepareChartData = () => {
    // Datos para el gráfico circular de distribución de gastos
    const pieData = Object.entries(totals.expenses).map(([category, amount]) => ({
      name: category.charAt(0).toUpperCase() + category.slice(1),
      value: amount
    })).filter(item => item.value > 0);

    // Datos para el gráfico de barras de ingresos vs gastos vs ganancias por orden
    const barData = filteredOrders.map(order => {
      const orderTotal = parseFloat(order.total) || 0;
      let orderExpenses = 0;
      
      if (order.gastos) {
        orderExpenses = Object.values(order.gastos).reduce(
          (sum, value) => sum + (parseFloat(value) || 0), 0
        );
      }
      
      const orderProfit = orderTotal - orderExpenses;
      
      return {
        name: `#${order.id.slice(0,5)}`,
        Total: orderTotal,
        Gastos: orderExpenses,
        Ganancia: orderProfit
      };
    });

    return { pieData, barData };
  };

  const chartData = prepareChartData();

  // Colores para los gráficos
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Función para exportar a CSV
  const exportToCSV = () => {
    const headers = [
      'ID Orden',
      'Cliente',
      'Fecha',
      'Total',
      'Mercadería',
      'Aduana',
      'Envío',
      'Extras',
      'Gastos Totales',
      'Ganancia',
      'Rentabilidad %'
    ].join(',');
    
    const rows = filteredOrders.map(order => {
      const orderTotal = parseFloat(order.total) || 0;
      const expenses = order.gastos || {};
      
      const mercaderia = parseFloat(expenses.mercaderia) || 0;
      const aduana = parseFloat(expenses.aduana) || 0;
      const envio = parseFloat(expenses.envio) || 0;
      const extras = parseFloat(expenses.extras) || 0;
      
      const totalExpenses = mercaderia + aduana + envio + extras;
      const profit = orderTotal - totalExpenses;
      const profitPercentage = orderTotal > 0 ? (profit / orderTotal) * 100 : 0;
      
      const orderDate = new Date(order.createdAt || order.created_at).toLocaleDateString();
      const cliente = `${order.shipping_address?.buyerInfo?.nombre || ''} ${order.shipping_address?.buyerInfo?.apellido || ''}`;
      
      return [
        order.id,
        cliente,
        orderDate,
        orderTotal.toFixed(2),
        mercaderia.toFixed(2),
        aduana.toFixed(2),
        envio.toFixed(2),
        extras.toFixed(2),
        totalExpenses.toFixed(2),
        profit.toFixed(2),
        profitPercentage.toFixed(2)
      ].join(',');
    }).join('\n');
    
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `contaduria_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Datos exportados correctamente');
  };

  // Renderizar gráfico circular
  const renderPieChart = () => (
    <div className="h-96 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <RechartsePieChart>
          <Pie
            data={chartData.pieData}
            cx="50%"
            cy="50%"
            labelLine={true}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            outerRadius={150}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
          <Legend />
        </RechartsePieChart>
      </ResponsiveContainer>
    </div>
  );

  // Renderizar gráfico de barras
  const renderBarChart = () => (
    <div className="h-96 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData.barData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
          <Legend />
          <BarChartIcon dataKey="Total" fill="#8884d8" />
          <BarChartIcon dataKey="Gastos" fill="#82ca9d" />
          <BarChartIcon dataKey="Ganancia" fill="#ffc658" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Contaduría</h2>
        
        <div className="flex space-x-4">
          <button 
            onClick={() => exportToCSV()}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Download size={18} className="mr-2" />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <DollarSign size={24} className="text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Ingresos Totales</h3>
          </div>
          <p className="text-3xl font-bold mt-2">${totals.revenue.toFixed(2)}</p>
          <p className="text-sm text-gray-500 mt-1">{filteredOrders.length} órdenes</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <DollarSign size={24} className="text-red-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Gastos Totales</h3>
          </div>
          <p className="text-3xl font-bold mt-2">${totals.expensesSum.toFixed(2)}</p>
          <div className="mt-2 text-sm text-gray-600 space-y-1">
            {Object.entries(totals.expenses).map(([category, amount]) => (
              <div key={category} className="flex justify-between">
                <span>{category.charAt(0).toUpperCase() + category.slice(1)}:</span>
                <span>${amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <TrendingUp size={24} className="text-green-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Ganancia Neta</h3>
          </div>
          <p className="text-3xl font-bold mt-2 text-green-600">${totals.profit.toFixed(2)}</p>
          <p className="text-sm text-gray-500 mt-1">
            {totals.expensesSum > 0 ? 
              `${(totals.profit / totals.expensesSum * 100).toFixed(2)}% sobre gastos` : 
              'Sin gastos registrados'}
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Percent size={24} className="text-purple-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Rentabilidad</h3>
          </div>
          <p className="text-3xl font-bold mt-2">{totals.profitPercentage.toFixed(2)}%</p>
          <p className="text-sm text-gray-500 mt-1">
            Ganancia / Ingresos
          </p>
        </div>
      </div>

      {/* Filtros y selectores de vista */}
      <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          {/* Filtro de fecha */}
          <div className="flex items-center space-x-2">
            <Calendar size={20} className="text-gray-400" />
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="border rounded-lg px-4 py-2"
            />
            <span>a</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="border rounded-lg px-4 py-2"
            />
            {(dateRange.start || dateRange.end) && (
              <button
                onClick={() => setDateRange({ start: '', end: '' })}
                className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Limpiar
              </button>
            )}
          </div>

          {/* Selectores de vista */}
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 rounded-lg ${
                viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tabla
            </button>
            <button
              onClick={() => setViewMode('pie')}
              className={`px-4 py-2 rounded-lg ${
                viewMode === 'pie' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <PieChart size={18} />
            </button>
            <button
              onClick={() => setViewMode('BarChartIcon')}
              className={`px-4 py-2 rounded-lg ${
                viewMode === 'BarChartIcon' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BarChartIcon size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Contenido según vista seleccionada */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {viewMode === 'table' && (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Pedido</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gastos</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ganancia</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rentabilidad</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => {
                const orderTotal = parseFloat(order.total) || 0;
                const expenses = order.gastos || {};
                
                const mercaderia = parseFloat(expenses.mercaderia) || 0;
                const aduana = parseFloat(expenses.aduana) || 0;
                const envio = parseFloat(expenses.envio) || 0;
                const extras = parseFloat(expenses.extras) || 0;
                
                const totalExpenses = mercaderia + aduana + envio + extras;
                const profit = orderTotal - totalExpenses;
                const profitPercentage = orderTotal > 0 ? (profit / orderTotal) * 100 : 0;
                
                const missingExpenses = getMissingExpenses(expenses, order);
                const orderDate = new Date(order.createdAt || order.created_at);

                return (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">#{order.id.slice(0,8)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {`${order.shipping_address?.buyerInfo?.nombre || ''} ${order.shipping_address?.buyerInfo?.apellido || ''}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {orderDate.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${orderTotal.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ${totalExpenses.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {Object.entries(expenses).map(([category, amount]) => (
                          <div key={category}>{category}: ${parseFloat(amount).toFixed(2)}</div>
                        ))}
                      </div>
                      {missingExpenses.length > 0 && (
                        <div className="flex items-center mt-1 text-xs text-amber-700 bg-amber-50 p-1 rounded-md">
                          <AlertTriangle size={14} className="mr-1" />
                          Pendiente: {missingExpenses.join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${profit.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`font-medium ${profitPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {profitPercentage.toFixed(2)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.status}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan="3" className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  TOTALES ({filteredOrders.length} órdenes)
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  ${totals.revenue.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  ${totals.expensesSum.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                  ${totals.profit.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                  {totals.profitPercentage.toFixed(2)}%
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
        
        {viewMode === 'pie' && renderPieChart()}
        {viewMode === 'BarChartIcon' && renderBarChart()}
      </div>
    </div>
  );
};

export default AdminAccountingPanel;